import React from 'react';
import { TextBoxComponent, TextAreaComponent } from '@syncfusion/ej2-react-inputs';
import { DatePickerComponent } from '@syncfusion/ej2-react-calendars';
import { Controller } from 'react-hook-form';
import { type CrudFormProps } from '../../components/CrudPage';
import { type HolidayDto } from '../../api';
import { FormDialog } from '../../components/FormDialog';
import type { Control, FormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type HolidayWithId = HolidayDto & { id: string };

// Format a Date to yyyy-MM-dd using its local calendar components. Using
// toISOString() here would shift the day for timezones ahead of UTC (e.g. a
// local midnight 2026-05-01 in Berlin becomes 2026-04-30 in UTC).
const toLocalIsoDate = (d: Date): string => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

interface FormProps {
  control: Control<HolidayWithId>;
  formState: FormState<HolidayWithId>;
}

const HolidayFormDialog: React.FC<CrudFormProps<HolidayWithId>> = ({
  value,
  onClose,
  onSave
}) => {
  const { t, i18n } = useTranslation();

  return (
    <FormDialog
      value={value}
      onClose={onClose}
      onSave={onSave}
      titleKey="dialogs.holiday"
    >
      {({ control, formState: { errors } }: FormProps) => (
        <>
          <div className="mb-4">
            <Controller
              name="name"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TextBoxComponent
                  {...field}
                  value={field.value ?? ''}
                  placeholder={`${t('holiday.form.namePlaceholder')} *`}
                  floatLabelType="Never"
                  cssClass={errors.name ? 'e-error' : ''}
                />
              )}
            />
            {errors.name && (
              <div className="text-red-500 text-sm mt-1">{errors.name.message?.toString()}</div>
            )}
          </div>

          <div className="mb-4">
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextAreaComponent
                  {...field}
                  value={field.value ?? ''}
                  placeholder={t('holiday.form.descriptionPlaceholder')}
                  width="100%"
                  rows={3}
                  floatLabelType="Never"
                  cssClass={errors.description ? 'e-error' : ''}
                />
              )}
            />
            {errors.description && (
              <div className="text-red-500 text-sm mt-1">{errors.description.message?.toString()}</div>
            )}
          </div>

          <div className="mb-4">
            <Controller
              name="holidayDate"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <DatePickerComponent
                  name={field.name}
                  value={field.value ? new Date(field.value as string) : undefined}
                  change={(e: { value?: Date | string | null }) => {
                    const v = e?.value;
                    if (v == null || v === '') {
                      field.onChange('');
                      return;
                    }
                    if (v instanceof Date) {
                      field.onChange(toLocalIsoDate(v));
                      return;
                    }
                    if (typeof v === 'string') {
                      const iso = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
                      if (iso) {
                        field.onChange(`${iso[1]}-${iso[2]}-${iso[3]}`);
                        return;
                      }
                      const de = v.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
                      if (de) {
                        const d = de[1].padStart(2, '0');
                        const m = de[2].padStart(2, '0');
                        field.onChange(`${de[3]}-${m}-${d}`);
                        return;
                      }
                    }
                    field.onChange('');
                  }}
                  blur={(e: React.FocusEvent<HTMLElement>) => {
                    field.onBlur();
                    const wrap =
                      e.target instanceof HTMLElement
                        ? e.target.closest('.e-datepicker') ?? e.target.closest('.e-date-wrapper')
                        : null;
                    const inst = (wrap as { ej2_instances?: { value?: Date }[] })?.ej2_instances?.[0];
                    const d = inst?.value;
                    if (d instanceof Date && !Number.isNaN(d.getTime())) {
                      field.onChange(toLocalIsoDate(d));
                    }
                  }}
                  placeholder={`${t('holiday.form.holidayDatePlaceholder')} *`}
                  format={i18n.language === 'de' ? 'dd.MM.yyyy' : 'MM/dd/yyyy'}
                  floatLabelType="Never"
                  cssClass={errors.holidayDate ? 'e-error' : ''}
                />
              )}
            />
            {errors.holidayDate && (
              <div className="text-red-500 text-sm mt-1">{errors.holidayDate.message?.toString()}</div>
            )}
          </div>
        </>
      )}
    </FormDialog>
  );
};

export default HolidayFormDialog;
